import { useCallback, useEffect, useMemo, useState, ReactNode } from "react";
import { Grid, GridSize, TextField, Autocomplete, Typography, Tooltip, ListItemIcon, SvgIcon, Popper, PopperProps } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { FormattedMessage, useIntl } from "react-intl";

import useEditedStyles from "../../../../hooks/useEditedStyles";
import { hexInputCheck, valueEdited } from "../utils";
import cids from "./standardCids";
import { useLocale } from "../../../../locales/LocaleProvider";

interface EventValue {
  alarm: string;
  recovery?: string | null;
}

interface ZoneEventProps {
  label?: string;
  value: EventValue;
  saved?: EventValue | null;
  onChange: (value: EventValue) => void;
  disabled?: boolean;
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  xl?: GridSize;
  width?: number;
  color?: "secondary" | "error" | "primary" | "info" | "success" | "warning" | undefined;
  noRecovery?: boolean;
  emptyRecovery?: "" | null;
}

interface CidValue {
  alarm: string;
  recovery: string | null;
  title: string;
  icon: ReactNode;
}

const AutocompletePopper = (props: PopperProps) => (
  <Popper {...props} style={{ ...props.style, width: "auto", minWidth: props.style?.width, maxWidth: "fit-content" }} placement="bottom-start" />
);

const filterOptions = createFilterOptions({
  stringify: (option: CidValue) => `${option.title} ${option.alarm}`,
});

const ZoneEvent = ({ label, value, saved, disabled, onChange, xs, sm, md, lg, xl, color, width, noRecovery, emptyRecovery }: ZoneEventProps) => {
  const [alarm, setAlarm] = useState(value.alarm);
  const [recovery, setRecovery] = useState(value.recovery);
  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();

  const { locale } = useLocale();

  const gridSizes = useMemo(() => {
    if (xs || sm || md || lg || xl) {
      return { xs, sm, md, lg, xl };
    } else {
      return { xs: 12, sm: 6, md: 4, lg: 3, xl: "auto" as GridSize };
    }
  }, [xs, sm, md, lg, xl]);

  const getDescr = useCallback(
    (descr) => {
      let key;
      if (locale === "hu") {
        key = "hu-hu";
      } else {
        key = "en-gb";
      }
      return descr[key] || descr["en-gb"] || descr[Object.keys(descr)[0]] || "";
    },
    [locale]
  );

  const CID = useMemo(
    () =>
      cids.map((event) => ({
        alarm: event.alarm.code,
        recovery: noRecovery ? null : event.recovery?.code || null,
        title: getDescr(event.alarm.descr),
        icon: event.icon,
      })),
    [getDescr, noRecovery]
  );

  const widthStyle = useMemo(() => {
    if (width) {
      return { width, minWidth: width };
    } else {
      return { minWidth: { xl: 180 } };
    }
  }, [width]);

  const onInputChange = useCallback(
    (event: React.SyntheticEvent, value: string, reason: string) => {
      setAlarm(value);

      // Programmatic Select
      if (reason === "reset") {
        const v = CID.find((c) => c.alarm === value);
        if (v) {
          onChange(v);
        }
      }
    },
    [onChange, setAlarm, CID]
  );

  const onClose = useCallback(
    (e: React.SyntheticEvent, reason: string) => {
      // Not Programmatic Reason
      if (reason === "blur") {
        const v = CID.find((c: any) => c.alarm === alarm);
        if (v) {
          onChange(v);
        } else {
          hexInputCheck(alarm, (alarm) => onChange({ alarm: alarm.substring(0, 4), recovery: null }));
        }
      }
    },
    [CID, onChange, alarm]
  );

  useEffect(() => {
    setAlarm(value.alarm);
    setRecovery(value.recovery || "");
  }, [value]);

  const selectedAlarm = cids.find((cid) => cid.alarm.code === alarm);

  return (
    <>
      <Grid item {...gridSizes} sx={{ position: "relative" }}>
        <Autocomplete
          inputValue={alarm}
          filterOptions={filterOptions}
          options={CID}
          disableClearable
          freeSolo
          PopperComponent={AutocompletePopper}
          getOptionLabel={(option: CidValue | string) => (typeof option === "string" ? option : option.alarm)}
          onInputChange={onInputChange}
          onClose={onClose}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label={label || formatMessage({ defaultMessage: "Alarm event" })}
                InputProps={{
                  ...params.InputProps,
                }}
                variant="filled"
                className={saved?.alarm !== alarm ? editedStyle.input : ""}
                sx={{ margin: 0, ...widthStyle }}
                size="small"
                color={color}
              />
            );
          }}
          renderOption={(props: object, option: CidValue) => (
            <Grid container {...props}>
              <Grid item xs={12}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <ListItemIcon>
                    <SvgIcon fontSize="small">{option.icon}</SvgIcon>
                  </ListItemIcon>
                  <FormattedMessage defaultMessage="{title} ({alarm})" values={{ alarm: option.alarm, title: option.title }} />
                </div>
              </Grid>
            </Grid>
          )}
          disabled={disabled}
          fullWidth
        />
        {selectedAlarm && (
          <Tooltip
            title={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <SvgIcon fontSize="small" sx={{ mr: 1 }}>
                  {selectedAlarm.icon}
                </SvgIcon>
                <Typography noWrap>{getDescr(selectedAlarm.alarm.descr)}</Typography>
              </div>
            }
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                position: "absolute",
                bottom: 4,
                right: "10px",
                maxWidth: "50%",
              }}
            >
              <SvgIcon fontSize="small" sx={{ mr: 0.5, width: 18, height: 18 }}>
                {selectedAlarm.icon}
              </SvgIcon>
              <Typography noWrap variant="caption">{getDescr(selectedAlarm.alarm.descr)}</Typography>
            </div>
          </Tooltip>
        )}
      </Grid>
      {!noRecovery && (
        <Grid item {...gridSizes}>
          <TextField
            label={formatMessage({ defaultMessage: "Recovery event" })}
            variant="filled"
            value={recovery}
            className={valueEdited(saved?.recovery, recovery, recovery || emptyRecovery) ? editedStyle.input : ""}
            inputProps={{ maxLength: 4, style: { textTransform: "uppercase" } }}
            onChange={(e) => hexInputCheck(e.target.value, setRecovery)}
            onBlur={() => onChange({ alarm, recovery })}
            sx={{ margin: 0, ...widthStyle }}
            size="small"
            color={color}
            disabled={disabled}
            autoComplete="off"
            fullWidth
          />
        </Grid>
      )}
    </>
  );
};

export default ZoneEvent;
